import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ShieldAlert } from 'lucide-react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-neutral-950 text-slate-100 flex items-center justify-center p-4 selection:bg-brand-500/30 selection:text-brand-300 font-sans">
          <div className="w-full max-w-lg p-6 sm:p-8 rounded-3xl bg-neutral-900/90 border border-red-500/30 shadow-2xl relative overflow-hidden backdrop-blur-xl space-y-6">
            <div className="absolute -top-24 -right-24 w-60 h-60 bg-red-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <span className="text-[11px] font-mono uppercase tracking-wider text-red-400 font-semibold block">
                  Application Resilience
                </span>
                <h1 className="text-xl sm:text-2xl font-bold font-display text-white">
                  Something went wrong
                </h1>
              </div>
            </div>

            <p className="text-sm text-slate-300 leading-relaxed">
              An unexpected issue occurred while rendering this view. Your saved quotes, onboardings, and system configurations are safe.
            </p>

            {this.state.error && (
              <div className="p-3 rounded-xl bg-black/50 border border-neutral-800 text-xs font-mono text-slate-400 overflow-x-auto max-h-32">
                <span className="text-red-400 font-bold block mb-1">Error Trace:</span>
                {this.state.error.message || String(this.state.error)}
              </div>
            )}

            <div className="pt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={this.handleReset}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 font-bold text-xs uppercase tracking-wider font-mono transition-all shadow-md active:scale-95 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reload Page</span>
              </button>

              <button
                type="button"
                onClick={this.handleGoHome}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-neutral-800 hover:bg-neutral-700 text-white font-semibold text-xs transition-all border border-neutral-700 active:scale-95 cursor-pointer"
              >
                <Home className="w-4 h-4 text-brand-400" />
                <span>Return to Home</span>
              </button>
            </div>

            <div className="pt-4 border-t border-neutral-800/80 flex items-center justify-between text-[11px] font-mono text-slate-500">
              <span className="flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 text-slate-400" />
                Randon Brown IT Infrastructure
              </span>
              <span>Self-Healing Engine</span>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
